import React from "react";
import { Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import { CountryExposure } from "api/api-risk-dashboard.d";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CountryExposureBarChartProps {
  countryData?: CountryExposure;
  isLoading?: boolean;
  error?: any;
}

const CountryExposureBarChart: React.FC<CountryExposureBarChartProps> = ({ 
  countryData: data,
  isLoading = false,
  error = null
}) => {
  const theme = useTheme();
  
  // Define chart color
  const barColor = "#1976D2"; // Primary blue
  
  // Transform API data for bar chart
  const createChartData = () => {
    if (!data?.data?.country) return null;
    
    const countryMap = data.data.country;
    
    // Sort countries by exposure value (descending)
    const sortedCountries = Object.entries(countryMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 9); // Limit to top 9 countries
    
    return {
      labels: sortedCountries.map(([country]) => country),
      datasets: [
        {
          label: 'Total Exposure',
          data: sortedCountries.map(([_, value]) => value),
          backgroundColor: barColor,
          borderColor: theme.palette.primary.dark,
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: theme.palette.primary.light,
        },
      ],
    };
  };

  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load country exposure data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ height: "calc(100% - 50px)" }}>
        <Bar 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            indexAxis: 'y', // Horizontal bar chart
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    return `$${context.parsed.x.toLocaleString()}`;
                  }
                }
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                grid: {
                  color: theme.palette.divider,
                },
                ticks: {
                  callback: function (value) {
                    return `$${(Number(value) / 1000000).toFixed(1)}M`;
                  }
                }
              },
              y: {
                grid: {
                  display: false,
                },
              }
            },
            animation: {
              duration: 1000
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default CountryExposureBarChart;
